// Where a signed-out visitor was going, kept in this tab across login (and an OAuth round-trip) so they land back
// there once signed in.

const KEY = 'lureo_return_after_login';

// A path left from a visit long ago is not where the person is going now
const MAX_AGE_MS = 30 * 60 * 1000;

const NOT_A_RETURN = /^\/(login|signup|reset-password)?(\?|#|$)/;

/** Remembers an in-app path (with its query) to return to after login; with `userId`, only for that user. */
export function rememberReturnPath(path: string, userId: string | null = null): void {
  if (NOT_A_RETURN.test(path)) return;
  try {
    sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now(), userId }));
  } catch {
    // Storage blocked: login lands on the dashboard as before
  }
}

/** The path remembered for `userId`, kept; null when there is none. */
export function peekReturnPath(userId: string | null | undefined): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { path, at, userId: owner } = JSON.parse(raw) as { path?: unknown; at?: unknown; userId?: unknown };
    if (typeof path !== 'string' || typeof at !== 'number' || Date.now() - at > MAX_AGE_MS) return null;
    if (typeof owner === 'string' && owner !== userId) return null;
    return path;
  } catch {
    return null;
  }
}

/** Forgets the remembered path. */
export function forgetReturnPath(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Storage blocked: there is nothing to forget
  }
}

/** The path remembered for `userId`, forgotten as it is read; null when there is none. */
export function takeReturnPath(userId: string | null | undefined): string | null {
  const path = peekReturnPath(userId);
  forgetReturnPath();
  return path;
}
